import React, { FC, Dispatch, SetStateAction } from 'react'
import { CustomField, LoadingButton } from 'src/ui'
import {
  Dialog,
  Button,
  Theme,
  DialogTitle,
  DialogContent,
  Box,
  InputLabel
} from '@material-ui/core'
import { styled, makeStyles } from '@material-ui/styles'
import { Form, Field } from 'react-final-form'
import { validateFormWithSchema } from 'src/core'
import * as yup from 'yup'
import {
  email,
  firstName,
  lastName,
  workspaceRoleId
} from 'src/helpers/schema'
import { WorkspaceRoles } from '../../../constants'

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginLeft: 10,
    marginBottom: 10,
    minWidth: 150
  },
  selectItem: {
    margin: '5px 5px 5px 0px',
    fontSize: 15,
    width: 150,
    height: 30,
    borderRadius: 5,
    backgroundColor: '#eeeeee'
  },
  hint: {
    fontSize: 12
  },
  addEmail: {
    width: 400,
    height: 40,
    paddingTop: 0,
    paddingBottom: 0
  }
}))

const StyledButton = styled(Button)(({ theme }) => ({
  '&:not(:last-child)': {
    marginRight: (theme as Theme).spacing(2)
  }
}))

const Root = styled('form')({
  width: '400px'
})

const FieldWrapper = styled('div')<Theme>(({ theme }) => ({
  marginBottom: theme.spacing(2)
}))

const userProfileSchema = yup.object().shape({
  firstName,
  lastName,
  email,
  workspaceRoleId
})

const ButtonsWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: (theme as Theme).spacing(1),
  marginBottom: (theme as Theme).spacing(1)
}))

interface TProps {
  onClose: () => void,
  setSearchEmail: Dispatch<SetStateAction<String>>,
  onSubmit: (user: {}) => void,
  loading: boolean,
  mutationError: boolean,
  isOpen: boolean,
  mode: Mode,
  data: any,
  workspaceName: String,
  workspaceRoles: any,
  shouldShowSkip?: boolean;
}

export enum Mode {
  ADD = 'ADD',
  UPDATE = 'UPDATE'
}

const validateForm = validateFormWithSchema({ schema: userProfileSchema })

export const AddWorkspaceMemberModal: FC<TProps> = ({
  onClose,
  onSubmit,
  loading,
  mutationError,
  mode,
  data,
  workspaceName,
  workspaceRoles,
  isOpen,
  setSearchEmail,
  shouldShowSkip
}) => {
  let filterWorkspaceRole = null
  let defaultWorkspaceRole = null

  if (workspaceRoles) {
    filterWorkspaceRole = workspaceRoles.filter(role => {
      const { name } = role
      if (name === WorkspaceRoles.MEMBER) {
        defaultWorkspaceRole = role
      }
      return name !== WorkspaceRoles.OWNER
    })
  }

  const classes = useStyles()

  const renderWorkspaceRoleItem = (workspaceRole: any) => {
    const { id, name } = workspaceRole
    return (
      <option
        className={classes.selectItem}
        key={id}
        value={id}>
        {name}
      </option>)
  }

  return (
    <Dialog
      onClose={onClose}
      open={isOpen}
    >
      <DialogTitle>
        {mode === Mode.ADD ? `Add User to ${workspaceName}` : 'Edit Member\'s role'}
      </DialogTitle>
      <DialogContent>
        <Form
          initialValues={{
            firstName: data?.user?.firstName || null,
            lastName: data?.user?.lastName || null,
            email: data?.user?.email || null,
            workspaceRoleId: mode === Mode.ADD ? defaultWorkspaceRole && defaultWorkspaceRole.id : data.workspaceRoleId,
            updateData: mode === Mode.ADD ? null : data
          }}
          onSubmit={onSubmit}
          validate={validateForm}
        >
          {({ handleSubmit, submitting }) => (
            <Root onSubmit={handleSubmit}>
              <Field name='email'>
                {({ input, meta }) => (
                  <FieldWrapper>
                    <CustomField
                      disabled={mode === Mode.UPDATE}
                      errorMessage={meta.touched && (meta.error || meta.submitError)}
                      fullWidth
                      inputProps={input}
                      label='Email'
                      onBlur={(data) => setSearchEmail(data.target.value)}
                    />
                  </FieldWrapper>
                )}
              </Field>
              <Field name='firstName'>
                {({ input, meta }) => (
                  <FieldWrapper>
                    <CustomField
                      disabled={mode === Mode.UPDATE || Boolean(data?.user?.firstName)}
                      errorMessage={meta.touched && (meta.error || meta.submitError)}
                      fullWidth
                      inputProps={input}
                      label='First Name'
                    />
                  </FieldWrapper>
                )}
              </Field>
              <Field name='lastName'>
                {({ input, meta }) => (
                  <FieldWrapper>
                    <CustomField
                      disabled={mode === Mode.UPDATE || Boolean(data?.user?.lastName)}
                      errorMessage={meta.touched && (meta.error || meta.submitError)}
                      fullWidth
                      inputProps={input}
                      label='Last Name'
                    />
                  </FieldWrapper>
                )}
              </Field>
              <InputLabel
                className={classes.hint}
                htmlFor='workspace-role-label'
              >Workspace Role
              </InputLabel>
              <Field
                className={classes.selectItem}
                component='select'
                name='workspaceRoleId'
              >
                {filterWorkspaceRole && filterWorkspaceRole.map(renderWorkspaceRoleItem)}
              </Field>
              <ButtonsWrapper>
                <LoadingButton
                  color='primary'
                  disabled={submitting || loading}
                  loading={submitting || loading}
                  style={{ marginRight: 20 }}
                  type='submit'
                  variant='contained'
                >
                  {mode === Mode.ADD ? 'Add User' : 'Update User'}
                </LoadingButton>
                <StyledButton
                  onClick={onClose}
                  type='button'
                  variant='contained'
                >
                  {shouldShowSkip ? 'Skip' : 'Cancel'}
                </StyledButton>
              </ButtonsWrapper>
            </Root>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
